const DEFAULT_SITE_URL = 'https://shuatiwang.com.cn';
const SITE_URL = (process.env.VUE_APP_SITE_URL || DEFAULT_SITE_URL).replace(/\/+$/, '');
const SITE_NAME = '刷题网';

const DEFAULT_SEO = {
  title: `${SITE_NAME} - 高中数学题库与在线组卷平台`,
  description: '刷题网提供高中数学题库、在线刷题、试卷下载与智能组卷服务，覆盖章节练习、真题训练和错题管理。',
  keywords: '高中数学题库,数学刷题,在线组卷,试卷下载,数学真题,刷题网',
  image: `${SITE_URL}/favicon.ico`
};

const ROUTE_SEO = {
  Home: {
    title: `${SITE_NAME} - 高中数学题库与在线组卷平台`,
    description: '专注高中数学学习，提供章节题库、真题训练、在线组卷和学习活动，提升刷题效率。',
    keywords: '高中数学,数学题库,在线刷题,数学真题,智能组卷'
  },
  home: {
    title: `${SITE_NAME} - 高中数学题库与在线组卷平台`
  },
  QuestionBank: {
    title: `数学题库 - ${SITE_NAME}`,
    description: '按章节与知识点筛选高中数学题目，支持高效刷题与错题复习。'
  },
  papers: {
    title: `试卷中心 - ${SITE_NAME}`,
    description: '浏览并下载数学试卷，支持按年级、难度和题型快速筛选。'
  },
  PaperDetail: {
    title: `试卷详情 - ${SITE_NAME}`,
    description: '查看试卷内容与题型分布，进行针对性练习与训练。'
  },
  AlbumList: {
    title: `专辑训练 - ${SITE_NAME}`,
    description: '通过专题专辑系统化训练数学重点题型。'
  },
  AlbumDetail: {
    title: `专辑详情 - ${SITE_NAME}`
  },
  ActivityList: {
    title: `学习活动 - ${SITE_NAME}`,
    description: '参与学习活动与任务，获取持续学习反馈。'
  },
  ActivityDetail: {
    title: `活动详情 - ${SITE_NAME}`
  },
  QuestionLists: {
    title: `题单中心 - ${SITE_NAME}`,
    description: '查看优质题单，按主题开展集中训练。'
  },
  QuestionListDetail: {
    title: `题单详情 - ${SITE_NAME}`
  },
  ForumList: {
    title: `学习论坛 - ${SITE_NAME}`,
    description: '在论坛交流解题思路、分享学习经验。'
  },
  ForumPostDetail: {
    title: `帖子详情 - ${SITE_NAME}`
  },
  About: {
    title: `关于我们 - ${SITE_NAME}`
  }
};

const META_KEYS = [
  { name: 'description', key: 'description' },
  { name: 'keywords', key: 'keywords' },
  { name: 'robots', key: 'robots' },
  { property: 'og:type', value: 'website' },
  { property: 'og:site_name', value: SITE_NAME },
  { property: 'og:title', key: 'title' },
  { property: 'og:description', key: 'description' },
  { property: 'og:url', key: 'canonical' },
  { property: 'og:image', key: 'image' },
  { name: 'twitter:card', value: 'summary_large_image' },
  { name: 'twitter:title', key: 'title' },
  { name: 'twitter:description', key: 'description' },
  { name: 'twitter:image', key: 'image' }
];

function upsertMetaTag(selectorAttr, selectorValue, content) {
  let meta = document.head.querySelector(`meta[${selectorAttr}="${selectorValue}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(selectorAttr, selectorValue);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function upsertCanonicalLink(href) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', href);
}

function upsertJsonLd(route, payload) {
  let script = document.getElementById('app-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.id = 'app-jsonld';
    document.head.appendChild(script);
  }

  const isHome = route.path === '/' || route.path === '/home';
  const structuredData = isHome
    ? {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      description: payload.description,
      inLanguage: 'zh-CN'
    }
    : {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: payload.title,
      url: payload.canonical,
      description: payload.description,
      inLanguage: 'zh-CN',
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: `${SITE_URL}/`
      }
    };

  script.textContent = JSON.stringify(structuredData);
}

function resolveCanonical(path) {
  const sanitizedPath = typeof path === 'string' ? path.split('?')[0].split('#')[0] : '/';
  const normalizedPath = sanitizedPath === '/home' ? '/' : sanitizedPath;
  return `${SITE_URL}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`;
}

function shouldNoIndex(route) {
  const hasProtectedMeta = route.matched.some(
    record => record.meta && (record.meta.requiresAuth || record.meta.requiresAdmin || record.meta.guest)
  );
  return hasProtectedMeta || String(route.path || '').startsWith('/admin');
}

function getSeoPayload(route) {
  const routeName = route.name ? String(route.name) : '';
  const routeSeo = ROUTE_SEO[routeName] || {};
  const fallbackTitle = routeName ? `${routeName} - ${SITE_NAME}` : DEFAULT_SEO.title;

  return {
    ...DEFAULT_SEO,
    ...routeSeo,
    title: routeSeo.title || route.meta?.title || fallbackTitle
  };
}

export function updateSeoForRoute(route) {
  if (typeof document === 'undefined' || !route) return;

  const seoPayload = getSeoPayload(route);
  seoPayload.canonical = resolveCanonical(route.path || '/');
  seoPayload.robots = shouldNoIndex(route) ? 'noindex, nofollow' : 'index, follow';

  document.title = seoPayload.title;
  upsertCanonicalLink(seoPayload.canonical);

  META_KEYS.forEach(item => {
    const content = item.value || seoPayload[item.key];
    if (!content) return;
    if (item.name) upsertMetaTag('name', item.name, content);
    if (item.property) upsertMetaTag('property', item.property, content);
  });

  upsertJsonLd(route, seoPayload);
}
